import { FormikDatePicker, useNotifications } from '@armhr/ui';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Field, Formik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Form } from 'react-router-dom';
import * as Yup from 'yup';

import { useApi } from '../../hooks/useApi';
import { extractFilename } from '../../utils/files';
import PdfReader from '../common/PdfReader';

const HandbookSignModal: React.FC<{
  refresh: () => void;
  onClose?: () => void;
  handbook: EmployeeHandbookAssignment;
}> = ({ refresh, onClose, handbook }) => {
  const api = useApi();
  const { showNotification } = useNotifications();
  const contentRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState<boolean>(true);
  const [step, setStep] = useState<number>(1);
  const [reachedBottom, setReachedBottom] = useState<boolean>(false);
  const [scrollProgress, setScrollProgress] = useState<number>(0);

  const handleHandbookSigned = async (values: {
    signature: string;
    signed_at: string;
  }) => {
    try {
      const { results } = await api.profiles.updateHandbookStatus({
        ...handbook,
        signed_at: values.signed_at,
        signature: values.signature,
        status: 'signed',
      });
      if (results.status === 'signed') {
        showNotification({
          message: `All done! Your acknowledgement and signature have been successfully recorded.`,
          severity: 'success',
        });
        refresh();
        window.scrollTo(0, 0);
      } else
        showNotification({
          message:
            'Sorry, your signature did not save. Please try again later.',
          severity: 'error',
        });
    } catch (e) {
      console.error(e);
      showNotification({
        message: 'Sorry, your signature did not save. Please try again later.',
        severity: 'error',
      });
    }
  };

  const checkScroll = useCallback(() => {
    if (handbook.status !== 'signed' && contentRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) {
        setScrollProgress(100);
        setReachedBottom(true);
        return;
      }
      const progress = Math.min((scrollTop / maxScroll) * 100, 100);
      setScrollProgress(progress);
      const remainingPx = maxScroll - scrollTop;
      if (remainingPx < 40) {
        setReachedBottom(true);
      }
    }
  }, [handbook.status]);

  // Re-check scroll position when PDF pages finish rendering and change the content height
  useEffect(() => {
    const el = contentRef.current;
    if (!el || reachedBottom) return;
    const observer = new ResizeObserver(() => checkScroll());
    observer.observe(el);
    return () => observer.disconnect();
  }, [reachedBottom, checkScroll]);

  return (
    <Dialog open={open} maxWidth="lg">
      <DialogTitle
        sx={{
          backgroundColor: '#f5f5f5',
          textAlign: 'center',
        }}
      >
        Handbook Acknowledgement for{' '}
        {extractFilename(handbook.presigned_url ?? '')}
      </DialogTitle>
      <Formik
        initialValues={{ signed_at: '', signature: '' }}
        validationSchema={Yup.object().shape({
          signature: Yup.string().required('Required'),
          signed_at: Yup.string().required('Required'),
        })}
        validateOnBlur
        onSubmit={(values) => {
          handleHandbookSigned(values);
          setOpen(false);
          onClose && onClose();
        }}
      >
        {({ errors, touched, submitForm }) => {
          return (
            <>
              <DialogContent ref={contentRef} onScroll={checkScroll}>
                <Form>
                  {step === 1 && (
                    <Slide
                      direction="left"
                      in={step === 1}
                      mountOnEnter
                      unmountOnExit
                      timeout={{
                        enter: 400,
                        exit: 400,
                      }}
                      container={contentRef.current}
                    >
                      <Box sx={{ width: '100%' }}>
                        <PdfReader
                          pdfUrl={handbook.presigned_url!}
                          pageWidth={500}
                        />
                      </Box>
                    </Slide>
                  )}
                  {step === 2 && (
                    <Slide
                      direction="left"
                      in={step === 2}
                      mountOnEnter
                      unmountOnExit
                      timeout={{
                        enter: 400,
                        exit: 400,
                      }}
                      container={contentRef.current}
                    >
                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={5}
                        sx={{ mt: 3 }}
                      >
                        <Box display="flex" flexDirection="column" gap={1}>
                          <Typography variant="h5">
                            I acknowledge that I have received and reviewed this
                            handbook.
                          </Typography>
                          <Typography variant="h5">
                            I understand that my electronic acknowledgment is
                            the legal equivalent of my signature.
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          maxWidth="100%"
                          justifyContent="space-between"
                          gap={2}
                        >
                          <Field
                            variant="standard"
                            fullWidth
                            name="signature"
                            as={TextField}
                            label="Signature"
                            error={touched.signature && !!errors.signature}
                          />
                          <FormikDatePicker
                            slotProps={{ textField: { variant: 'standard' } }}
                            name="signed_at"
                            label="Date signed"
                            error={!!errors.signed_at}
                          />
                        </Box>
                      </Box>
                    </Slide>
                  )}
                </Form>
              </DialogContent>
              <DialogActions
                sx={{
                  position: 'relative',
                  borderTop: '1px solid #e0e0e0',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 2,
                }}
              >
                {/* Center: Progress indicator with label */}
                {step === 1 && (
                  <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                        textAlign: 'center',
                      }}
                    >
                      {reachedBottom
                        ? 'Document reviewed — ready to continue'
                        : 'Please scroll to review the entire document'}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 180 }}>
                        <LinearProgress
                          variant="determinate"
                          value={reachedBottom ? 100 : scrollProgress}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 3,
                              backgroundColor: reachedBottom
                                ? 'success.main'
                                : 'primary.main',
                            },
                          }}
                        />
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          minWidth: 36,
                          textAlign: 'right',
                        }}
                      >
                        {reachedBottom
                          ? '100%'
                          : `${Math.round(scrollProgress)}%`}
                      </Typography>
                    </Stack>
                  </Stack>
                )}

                {/* Right: Buttons (absolute positioned) */}
                <Stack
                  direction="row"
                  gap={1}
                  sx={{ position: 'absolute', right: 16 }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setOpen(false);
                      onClose && onClose();
                    }}
                  >
                    Close
                  </Button>

                  {step === 1 && (
                    <Tooltip
                      placement="top"
                      title={
                        !reachedBottom
                          ? 'Must view entire document to proceed'
                          : null
                      }
                    >
                      <div>
                        <Button
                          variant="contained"
                          disabled={!reachedBottom}
                          onClick={() => setStep(2)}
                        >
                          Next
                        </Button>
                      </div>
                    </Tooltip>
                  )}
                  {step === 2 && (
                    <>
                      <Button variant="outlined" onClick={() => setStep(1)}>
                        Back
                      </Button>
                      <Button variant="contained" onClick={submitForm}>
                        Sign and submit
                      </Button>
                    </>
                  )}
                </Stack>
              </DialogActions>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default HandbookSignModal;
